'use client'

import { useEffect } from 'react'

import { getNavigationItems } from '@common/config/navigation'

import { PORTAL_ONBOARDING_DOM_TARGET, type PortalOnboardingStepId } from '../portal-onboarding.const'
import type { PortalOnboardingTargetAnnotationsOptions } from '../portal-onboarding.interface'

const SIDE_NAV_ITEM_TO_STEP: Record<string, PortalOnboardingStepId> = {
  portal: 'portal',
  vision: 'vision',
  modules: 'modules',
  gateway: 'gateway',
  insights: 'insights',
  academy: 'academy',
}

const NAVBAR_ROUTE_TO_STEP: Record<string, PortalOnboardingStepId> = {
  'service-map': 'serviceMap',
  grafts: 'grafts',
}

function annotateSideNavigation(container: HTMLElement) {
  const navigationItems = getNavigationItems()
  const links = Array.from(container.querySelectorAll<HTMLElement>('a, button'))

  navigationItems.forEach((item, index) => {
    const stepId = SIDE_NAV_ITEM_TO_STEP[item.id]
    const targetId = stepId ? PORTAL_ONBOARDING_DOM_TARGET[stepId] : undefined
    const element = links[index]
    if (!targetId || !element) return
    element.setAttribute('data-portal-onboarding', targetId)
  })
}

function annotateNavbarItems(container: HTMLElement) {
  const links = Array.from(container.querySelectorAll<HTMLElement>('a[href]'))

  links.forEach((link) => {
    const href = link.getAttribute('href')
    if (!href) return

    const url = new URL(href, window.location.origin)
    const segments = url.pathname.split('/').filter(Boolean)
    const route = segments.length >= 2 ? segments[segments.length - 1] : segments[0]
    const stepId = route ? NAVBAR_ROUTE_TO_STEP[route] : undefined
    const targetId = stepId ? PORTAL_ONBOARDING_DOM_TARGET[stepId] : undefined

    if (targetId) {
      link.setAttribute('data-portal-onboarding', targetId)
    }
  })
}

export function usePortalOnboardingTargetAnnotations({
  sideNavContainerRef,
  navbarContainerRef,
  enabled = true,
}: PortalOnboardingTargetAnnotationsOptions) {
  useEffect(() => {
    if (!enabled) return

    const annotate = () => {
      if (sideNavContainerRef.current) {
        annotateSideNavigation(sideNavContainerRef.current)
      }

      if (navbarContainerRef.current) {
        annotateNavbarItems(navbarContainerRef.current)
      }
    }

    annotate()

    const sideNavObserver =
      sideNavContainerRef.current && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => annotate())
        : null

    const navbarObserver =
      navbarContainerRef.current && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => annotate())
        : null

    sideNavObserver?.observe(sideNavContainerRef.current!, { childList: true, subtree: true })
    navbarObserver?.observe(navbarContainerRef.current!, { childList: true, subtree: true })

    return () => {
      sideNavObserver?.disconnect()
      navbarObserver?.disconnect()
    }
  }, [enabled, navbarContainerRef, sideNavContainerRef])
}
